/**
 * useConditionalLogic
 *
 * Shared runtime engine for `FormFieldDefinition.conditionalLogic` rules.
 * Evaluates SHOW / HIDE / ENABLE / DISABLE / REQUIRE / UNREQUIRE / SET_VALUE
 * actions against live form data and returns per-field flags.
 *
 * Extracted from `FormPreviewModal.evaluateConditionalLogic/evaluateFieldEnabled`
 * so that builder preview, runtime form page, and any future renderer share
 * one evaluator.
 */

import { useMemo } from 'react';
import type {
  FormFieldDefinition,
  ConditionalLogicRule,
} from '../types/formBuilder';
import type { FieldValue } from '../components/workspace/dynamicFormFieldUtils';

export interface ConditionalLogicResult {
  /** fieldId -> visibility flag (default true) */
  visibility: Record<string, boolean>;
  /** fieldId -> enabled flag (default true) */
  enabled: Record<string, boolean>;
  /** fieldId -> required override (undefined = respect field.mandatory) */
  requiredOverrides: Record<string, boolean | undefined>;
  /** fieldId -> forced value from SET_VALUE action */
  forcedValues: Record<string, string>;
  isVisible: (fieldId: string) => boolean;
  isEnabled: (fieldId: string) => boolean;
  isRequired: (field: FormFieldDefinition) => boolean;
}

const evaluateOperator = (
  operator: ConditionalLogicRule['operator'],
  dependent: unknown,
  compareValue: string | undefined,
): boolean => {
  const dv = dependent as unknown;
  const cv = compareValue ?? '';
  const toStr = (v: unknown) => (v == null ? '' : String(v));

  switch (operator) {
    case 'EQUALS':
      return toStr(dv) === cv;
    case 'NOT_EQUALS':
      return toStr(dv) !== cv;
    case 'IS_EMPTY':
      return dv == null || toStr(dv).trim() === '' || (Array.isArray(dv) && dv.length === 0);
    case 'IS_NOT_EMPTY':
      return dv != null && toStr(dv).trim() !== '' && !(Array.isArray(dv) && dv.length === 0);
    case 'CONTAINS':
      if (Array.isArray(dv)) return dv.map(toStr).includes(cv);
      return toStr(dv).includes(cv);
    case 'NOT_CONTAINS':
      if (Array.isArray(dv)) return !dv.map(toStr).includes(cv);
      return !toStr(dv).includes(cv);
    case 'GREATER_THAN':
      return Number(dv) > Number(cv);
    case 'LESS_THAN':
      return Number(dv) < Number(cv);
    case 'GREATER_OR_EQUAL':
      return Number(dv) >= Number(cv);
    case 'LESS_OR_EQUAL':
      return Number(dv) <= Number(cv);
    case 'IN_LIST': {
      const list = cv.split(',').map((s) => s.trim());
      return list.includes(toStr(dv));
    }
    case 'NOT_IN_LIST': {
      const list = cv.split(',').map((s) => s.trim());
      return !list.includes(toStr(dv));
    }
    default:
      return false;
  }
};

export function useConditionalLogic(
  fields: FormFieldDefinition[] | undefined,
  formData: Record<string, FieldValue>,
): ConditionalLogicResult {
  return useMemo(() => {
    const visibility: Record<string, boolean> = {};
    const enabled: Record<string, boolean> = {};
    const requiredOverrides: Record<string, boolean | undefined> = {};
    const forcedValues: Record<string, string> = {};

    (fields ?? []).forEach((field) => {
      let vis = true;
      let en = true;
      let reqOverride: boolean | undefined;

      const rules = field.conditionalLogic ?? [];
      for (const rule of rules) {
        if (!rule.enabled) continue;
        const depValue = formData[rule.dependsOnField];
        const matched = evaluateOperator(rule.operator, depValue, rule.compareValue);

        switch (rule.action) {
          case 'SHOW':
            vis = matched ? true : false;
            break;
          case 'HIDE':
            vis = matched ? false : true;
            break;
          case 'ENABLE':
            en = matched;
            break;
          case 'DISABLE':
            en = matched ? false : true;
            break;
          case 'REQUIRE':
            if (matched) reqOverride = true;
            break;
          case 'UNREQUIRE':
            if (matched) reqOverride = false;
            break;
          case 'SET_VALUE':
            if (matched && rule.actionValue != null) {
              forcedValues[field.internalName] = rule.actionValue;
            }
            break;
          default:
            break;
        }
      }

      visibility[field.fieldId] = vis;
      enabled[field.fieldId] = en;
      requiredOverrides[field.fieldId] = reqOverride;
    });

    return {
      visibility,
      enabled,
      requiredOverrides,
      forcedValues,
      isVisible: (fieldId: string) => visibility[fieldId] !== false,
      isEnabled: (fieldId: string) => enabled[fieldId] !== false,
      isRequired: (field: FormFieldDefinition) => {
        const override = requiredOverrides[field.fieldId];
        return override === undefined ? !!field.mandatory : override;
      },
    };
  }, [fields, formData]);
}
